// services/vidalService.ts
import axios, { AxiosInstance } from 'axios';
import { XMLParser } from 'fast-xml-parser';

// Configuration de base
const apiKey: string = process.env.VUE_APP_VIDAL_API_KEY as string;
const baseUrl: string = process.env.VUE_APP_VIDAL_BASE_URL as string;
const appId: string = process.env.VUE_APP_VIDAL_APP_ID as string;

if (!apiKey || !baseUrl || !appId) {
  throw new Error('Les variables d\'environnement VIDAL ne sont pas définies.');
}

const axiosInstance: AxiosInstance = axios.create({
  // baseURL: process.env.NODE_ENV === 'development' ? '/api/vidal' : baseUrl,
  baseURL: baseUrl,
  timeout: 30000,
  headers: { 'Content-Type': 'application/xml' },
  withCredentials: false,
});

const parser = new XMLParser({ 
  ignoreAttributes: false,
  parseAttributeValue: true,
  trimValues: true
});

// ================== INTERFACES COMMUNES ==================

export interface VidalProduct {
  cip: string | null;
  libelle: string;
}

export interface ApiResponse {
  products: VidalProduct[];
}

export interface VidalProductDetailed {
  cip: string | null;
  libelle: string;
  dci: string | null;
  forme: string | null;
  dosage: string | null;
  posologie: string | null;
  duree: string | null;
  indications: string[] | null;
  contrindications: string[] | null;
  productId?: string | null;
  securityScore?: number;
  securityLevel?: 'EXCELLENT' | 'GOOD' | 'WARNING' | 'DANGER';
  patientAlerts?: { level: string; message: string }[];
}

export interface ApiResponseDetailed {
  products: VidalProductDetailed[];
}

export interface PatientContext {
  age?: number;
  gender?: 'MALE' | 'FEMALE' | 'UNKNOWN';
  weightKg?: number;
  allergies?: string[];
  pathologies?: string[];
  medications?: string[];
}

export interface PrescriptionAssistanceResult {
  products: VidalProductDetailed[];
  securityResults: any;
  noResultInfo?: {
    message: string;
    searchTerm: string;
    patientAge?: number;
  };
}

// ================== FONCTIONS UTILITAIRES ==================

function extractCipFromEntry(entry: any): string | null {
  if (!entry) return null;
  
  const candidates = [
    entry['vidal:cip'], entry['vidal:cip13'], entry['vidal:codeCIP13'], 
    entry['vidal:codeCIP'], entry['cip'], entry['cip13'], 
    entry['codeCIP13'], entry['codeCIP'],
  ].filter(Boolean);
  
  if (candidates.length > 0) return String(candidates[0]);

  const props = entry['vidal:properties'] || entry.properties || entry.content;
  if (props) {
    const nested = [
      props['vidal:cip'], props['vidal:cip13'], props['vidal:codeCIP13'], 
      props['vidal:codeCIP'], props['cip'], props['cip13'], 
      props['codeCIP13'], props['codeCIP'],
    ].filter(Boolean);
    if (nested.length > 0) return String(nested[0]);
  }
  
  return null;
}

const fetchCipFromPackages = async (packageUrl: string): Promise<string | null> => {
  try {
    const response = await axiosInstance.get(packageUrl, {
      params: { app_id: appId, app_key: apiKey },
      responseType: 'text',
    });

    const jsonObj = parser.parse(response.data);
    const entries = jsonObj?.feed?.entry || jsonObj?.entry || [];
    const array = Array.isArray(entries) ? entries : [entries];
    
    for (const e of array) {
      const cip = extractCipFromEntry(e);
      if (cip) return cip;
    }
    return null;
  } catch (error) {
    console.warn('Erreur récupération CIP:', error);
    return null;
  }
};

// ================== SECTION 1: RECHERCHE D'ÉQUIVALENTS DCI ==================

async function searchEquivalentByVMPOptimized(query: string, maxResults: number = 500): Promise<ApiResponse> {
  try {
    const allVmps: any[] = [];
    let page = 1;
    const perPage = 100;
    
    while (allVmps.length < 50) {
      const vmpResponse = await axiosInstance.get('/vmps', {
        params: { 
          app_id: appId, 
          app_key: apiKey,
          q: query,
          'start-page': page,
          'page-size': perPage
        },
        responseType: 'text'
      });

      const vmpData = parser.parse(vmpResponse.data);
      const vmpEntries = vmpData?.feed?.entry || [];
      const vmps = Array.isArray(vmpEntries) ? vmpEntries : [vmpEntries].filter(Boolean);
      
      if (!vmps.length) break;
      
      allVmps.push(...vmps);
      if (vmps.length < perPage) break;
      page++;
      
      if (page > 2) break;
    }
    
    if (!allVmps.length) return { products: [] };

    const allProducts: any[] = [];
    const topVmps = allVmps.slice(0, 15);

    for (const vmp of topVmps) {
      const vmpId = vmp['vidal:id'] || vmp.id;
      if (!vmpId) continue;

      try {
        const productsResponse = await axiosInstance.get(`/vmp/${vmpId}/products`, {
          params: { 
            app_id: appId, 
            app_key: apiKey,
            'start-page': 1, 
            'page-size': 200
          },
          responseType: 'text'
        });

        const productsData = parser.parse(productsResponse.data);
        const productEntries = productsData?.feed?.entry || [];
        const pageItems = Array.isArray(productEntries) ? productEntries : [productEntries].filter(Boolean);
        
        if (pageItems.length > 0) {
          allProducts.push(...pageItems);
        }
        
        if (allProducts.length >= maxResults) break;
      } catch (e) {
        continue;
      }
    }

    if (!allProducts.length) return { products: [] };

    const resultPromises = allProducts.map(async (product: any) => {
      const libelle = product.title || product['title'] || 'Produit sans nom';
      const cip = extractCipFromEntry(product) || await fetchCipFromPackages(
        product.link?.find((l: any) => l['@_rel'] === 'packages')?.['@_href']
      );
      
      return { cip, libelle };
    });

    const processed = await Promise.all(resultPromises);
    
    const seen = new Set<string>();
    const dedup = processed.filter(p => {
      const key = `${(p.libelle || '').toLowerCase()}_${p.cip || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, maxResults);
    
    return { products: dedup };
  } catch (e) {
    console.error('Erreur recherche VMP:', e);
    return { products: [] };
  }
}

export const searchMedicaments = async (query: string): Promise<ApiResponse> => {
  try {
    const normalized = (query || '').trim();
    if (!normalized || normalized.toLowerCase() === 'vide') {
      return { products: [] };
    }

    return await searchEquivalentByVMPOptimized(normalized, 500);
  } catch (error) {
    console.error('Erreur recherche équivalents:', error);
    throw error;
  }
};

// ================== SECTION 2: AIDE À LA PRESCRIPTION AVEC SÉCURISATION ==================

export const prescriptionAssistanceWithVidalSecurity = async (
  pathology: string,
  patient: PatientContext,
  maxResults: number = 50
): Promise<PrescriptionAssistanceResult> => {
  try {
    // Recherche des indications correspondant à la pathologie
    const indicationsResponse = await axiosInstance.get('/indications', {
      params: { 
        app_id: appId, 
        app_key: apiKey,
        q: pathology,
        'page-size': 10
      },
      responseType: 'text',
    });

    const indicationsData = parser.parse(indicationsResponse.data);
    const indicationEntries = indicationsData?.feed?.entry || [];
    const indications = Array.isArray(indicationEntries) ? indicationEntries : [indicationEntries];

    if (!indications.length) {
      return {
        products: [],
        securityResults: {},
        noResultInfo: {
          message: `Aucune indication trouvée pour "${pathology}"`,
          searchTerm: pathology,
          patientAge: patient.age
        }
      };
    }

    const allProducts: VidalProductDetailed[] = [];
    const topIndications = indications.slice(0, 5);
    
    // Pour chaque indication, récupérer les produits associés
    for (const indication of topIndications) {
      const indicationId = indication['vidal:id'] || indication.id;
      if (!indicationId) continue;

      try {
        const perPage = 100;
        let page = 1;
        
        while (allProducts.length < maxResults) {
          const productsResponse = await axiosInstance.get(`/indication/${indicationId}/products`, {
            params: { 
              app_id: appId, 
              app_key: apiKey,
              'start-page': page,
              'page-size': perPage
            },
            responseType: 'text',
          });

          const productsData = parser.parse(productsResponse.data);
          const productEntries = productsData?.feed?.entry || [];
          const pageItems = Array.isArray(productEntries) ? productEntries : [productEntries].filter(Boolean);
          
          if (!pageItems.length) break;
          
          // Pour chaque produit, récupérer les informations détaillées
          for (const product of pageItems) {
            const productId = product['vidal:id'] || product.id;
            
            // Récupérer les contre-indications
            let contrindications: string[] = [];
            try {
              const contraResponse = await axiosInstance.get(`/product/${productId}/contraindications`, {
                params: { app_id: appId, app_key: apiKey },
                responseType: 'text',
              });
              
              const contraData = parser.parse(contraResponse.data);
              const contraEntries = contraData?.feed?.entry || [];
              const contraArray = Array.isArray(contraEntries) ? contraEntries : [contraEntries];
              contrindications = contraArray.map((entry: any) => entry.title || entry['vidal:name']).filter(Boolean);
            } catch (error) {
              console.warn(`Erreur récupération contre-indications pour ${productId}:`, error);
            }

            const vidalProduct: VidalProductDetailed = {
              cip: extractCipFromEntry(product),
              libelle: product.title || product['vidal:name'] || 'Produit sans nom',
              productId: productId,
              dci: product['vidal:dci'] || product['vidal:activeSubstance'] || null,
              forme: product['vidal:galenicForm'] || product['vidal:galenic'] || null,
              dosage: product['vidal:dosage'] || product['vidal:strength'] || null,
              posologie: product['vidal:posology'] || null,
              duree: product['vidal:duration'] || null,
              indications: indication.title ? [indication.title] : null,
              contrindications: contrindications.length > 0 ? contrindications : null
            };
            
            // Appliquer l'analyse de sécurité patient
            const securityAnalysis = await analyzePatientSecurity(vidalProduct, patient);
            vidalProduct.securityScore = securityAnalysis.overallScore;
            vidalProduct.securityLevel = securityAnalysis.securityLevel;
            vidalProduct.patientAlerts = securityAnalysis.patientAlerts;
            
            allProducts.push(vidalProduct);
          }
          
          if (allProducts.length >= maxResults) break;
          page++;
        }
      } catch (error) {
        console.error(`Erreur récupération produits pour indication ${indicationId}:`, error);
        continue;
      }
    }

    // Trier par score de sécurité (du plus élevé au plus bas)
    allProducts.sort((a, b) => (b.securityScore || 0) - (a.securityScore || 0));

    return {
      products: allProducts.slice(0, maxResults),
      securityResults: {
        patientContext: patient,
        totalProducts: allProducts.length
      }
    };

  } catch (error) {
    console.error('Erreur aide à la prescription:', error);
    return {
      products: [],
      securityResults: {},
      noResultInfo: {
        message: `Erreur de recherche pour "${pathology}"`,
        searchTerm: pathology,
        patientAge: patient.age
      }
    };
  }
};

// ================== ANALYSE DE SÉCURITÉ PATIENT ==================

async function analyzePatientSecurity(product: VidalProductDetailed, patient: PatientContext): Promise<any> {
  let overallScore = 100;
  const alerts: { level: string; message: string }[] = [];
  let securityLevel: 'EXCELLENT' | 'GOOD' | 'WARNING' | 'DANGER' = 'EXCELLENT';

  // Vérification des allergies
  if (patient.allergies && patient.allergies.length > 0 && product.contrindications) {
    for (const allergy of patient.allergies) {
      const normalizedAllergy = allergy.toLowerCase().trim();
      for (const contra of product.contrindications) {
        if (contra.toLowerCase().includes(normalizedAllergy)) {
          overallScore -= 30;
          alerts.push({
            level: 'DANGER',
            message: `Allergie détectée: ${allergy} - Contre-indication: ${contra}`
          });
        }
      }
    }
  }

  // Vérification des pathologies
  if (patient.pathologies && patient.pathologies.length > 0 && product.contrindications) {
    for (const pathology of patient.pathologies) {
      const normalizedPathology = pathology.toLowerCase().trim();
      for (const contra of product.contrindications) {
        if (contra.toLowerCase().includes(normalizedPathology)) {
          overallScore -= 25;
          alerts.push({
            level: 'WARNING',
            message: `Pathologie incompatible: ${pathology} - Contre-indication: ${contra}`
          });
        }
      }
    }
  }

  // Vérification de l'âge
  if (patient.age) {
    if (patient.age < 12) {
      // Vérifier si le produit est adapté aux enfants
      if (product.contrindications && product.contrindications.some(contra => 
        contra.toLowerCase().includes('enfant') || contra.toLowerCase().includes('enfant')
      )) {
        overallScore -= 15;
        alerts.push({
          level: 'WARNING',
          message: `Ce médicament nécessite une attention particulière pour les enfants de ${patient.age} ans`
        });
      }
    } else if (patient.age > 65) {
      // Vérifier si le produit est adapté aux personnes âgées
      if (product.contrindications && product.contrindications.some(contra => 
        contra.toLowerCase().includes('âgé') || contra.toLowerCase().includes('personne âgée')
      )) {
        overallScore -= 10;
        alerts.push({
          level: 'INFO',
          message: `Adaptation posologique possible pour les personnes de ${patient.age} ans`
        });
      }
    }
  }

  // Vérification du poids
  if (patient.weightKg) {
    // Logique spécifique pour l'adaptation posologique selon le poids
    if (patient.weightKg < 50) {
      alerts.push({
        level: 'INFO',
        message: `Patient de ${patient.weightKg}kg - Vérifier l'adaptation posologique`
      });
    } else if (patient.weightKg > 100) {
      alerts.push({
        level: 'INFO',
        message: `Patient de ${patient.weightKg}kg - Possibilité d'adaptation posologique`
      });
    }
  }

  // Détermination du niveau de sécurité
  if (overallScore >= 80) {
    securityLevel = 'EXCELLENT';
  } else if (overallScore >= 60) {
    securityLevel = 'GOOD';
  } else if (overallScore >= 40) {
    securityLevel = 'WARNING';
  } else {
    securityLevel = 'DANGER';
  }

  return {
    overallScore: Math.max(0, overallScore),
    securityLevel,
    patientAlerts: alerts
  };
}

// ================== FONCTION DE RECHERCHE DÉTAILLÉE ==================

export const searchMedicamentsDetailed = async (query: string): Promise<ApiResponseDetailed> => {
  try {
    const response = await axiosInstance.get('/products', {
      params: { q: query, app_id: appId, app_key: apiKey },
      responseType: 'text',
    });

    if (!response.data || response.data.trim() === '') {
      return { products: [] };
    }

    const jsonObj = parser.parse(response.data);
    const entries = jsonObj.feed?.entry || [];
    const productsArray = Array.isArray(entries) ? entries : [entries];

    const resultPromises = productsArray.map(async (product: any) => ({
      cip: extractCipFromEntry(product),
      libelle: product.title || product['title'] || 'Produit sans nom',
      dci: product['vidal:dci'] || null,
      forme: product['vidal:galenic'] || null,
      dosage: product['vidal:dosage'] || null,
      posologie: product['vidal:posology'] || null,
      duree: product['vidal:duration'] || null,
      indications: product['vidal:indication'] ? [product['vidal:indication']] : null,
      contrindications: product['vidal:contraindication'] ? [product['vidal:contraindication']] : null
    }));

    return { products: await Promise.all(resultPromises) };
  } catch (error) {
    console.error('Erreur recherche détaillée:', error);
    throw error;
  }
};

// Export des fonctions principales
export {
  searchMedicaments as searchEquivalentsByDCI,
  prescriptionAssistanceWithVidalSecurity as searchPrescriptionAssistance
};